import React, { useState, useCallback } from 'react';
import CustomFieldRenderer from './CustomFieldRenderer';
import { CustomField, CustomFieldOption, CustomFieldDependencyLevel } from '../hooks/useCompanyCustomFields';
import './CustomFieldSection.css';

interface CustomFieldSectionProps {
  customFields: CustomField[];
  customFieldValues: { [key: number]: any };
  customFieldErrors: { [key: number]: string };
  onCustomFieldChange: (fieldId: number, value: any) => void;
  onLoadOptions: (fieldId: number) => Promise<CustomFieldOption[]>;
  onLoadDependencyLevels?: (fieldId: number) => Promise<CustomFieldDependencyLevel[]>;
  loading?: boolean;
  error?: string | null;
  entityName?: string;
}

const CustomFieldSection: React.FC<CustomFieldSectionProps> = ({
  customFields,
  customFieldValues,
  customFieldErrors,
  onCustomFieldChange,
  onLoadOptions,
  onLoadDependencyLevels,
  loading = false,
  error = null,
  entityName = 'record'
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  if (customFields.length === 0) {
    return null;
  }

  const requiredFieldsCount = customFields.filter(f => f.is_required).length;

  return (
    <div className="custom-field-section-wrapper">
      <div className="custom-field-section-header" onClick={handleToggleExpand}>
        <div className="d-flex align-items-center justify-content-between w-100">
          <div className="d-flex align-items-center">
            <i className={`ti ti-chevron-${isExpanded ? 'down' : 'right'} me-2 expand-icon`}></i>
            <h6 className="mb-0 fw-semibold">
              <i className="ti ti-forms me-2"></i>
              Custom Fields
              {loading && (
                <span className="spinner-border spinner-border-sm ms-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </span>
              )}
            </h6>
          </div>
          <div className="d-flex align-items-center">
            <small className="text-muted me-2">
              {customFields.length} field{customFields.length !== 1 ? 's' : ''}
              {requiredFieldsCount > 0 && (
                <span className="text-danger ms-1">
                  ({requiredFieldsCount} required)
                </span>
              )}
            </small>
            <i className="ti ti-info-circle text-muted" title={`Additional information for ${entityName}`}></i>
          </div>
        </div>
      </div>

      <div className={`custom-field-section-content ${isExpanded ? 'expanded' : 'collapsed'}`}>
        <div className="custom-field-section-body">
          {error && (
            <div className="alert alert-warning mb-3">
              <i className="ti ti-alert-triangle me-2"></i>
              {error}
            </div>
          )}

          <div className="row">
            {customFields.map((field) => (
              <div 
                key={field.custom_field_id} 
                className={
                  ['TEXTAREA', 'RADIO_OPTION', 'DEPENDENT_FIELD'].includes(field.custom_field_data_type.custom_field_data_type_key) 
                    ? "col-12" 
                    : "col-md-6"
                }
              >
                <CustomFieldRenderer
                  field={field}
                  value={customFieldValues[field.custom_field_id]}
                  onChange={onCustomFieldChange}
                  onLoadOptions={onLoadOptions}
                  onLoadDependencyLevels={onLoadDependencyLevels}
                  error={customFieldErrors[field.custom_field_id]}
                />
              </div>
            ))}
          </div>

          {requiredFieldsCount > 0 && (
            <div className="mt-3 pt-2 border-top">
              <small className="text-muted">
                <i className="ti ti-info-circle me-1"></i>
                Fields marked with <span className="text-danger">*</span> are required
              </small>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomFieldSection;
